import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@mui/material';

import { PsButtonTuner, PsCheckboxTuner } from 'stencilSetup';
import { SearchResultsContent } from './SearchResultsContent';
import { selectSubscriberDetails } from 'enrollment/features/Profile/services';
import { displayDependentAvatar } from 'enrollment/features/Family/FamilyDetailsPage/components';
import { selectDependentDetails } from 'enrollment/features/Family/services';
import { getDependentAge } from 'enrollment/features/Shared/FamilySelect/utils';
import { ProvideInfoDrawer } from '../ProvideInfoDrawer/ProvideInfoDrawer';
import { OrbitDrawer } from 'enrollment/components/Shared/OrbitDrawer';
import {
  ProviderFamilyBoxStyled,
  SearchResultFamilyProviderStyled,
} from './SearchResultsPCPDrawerStyled';
import { selectProviderApiData, setProviderApi } from '../services';
import {
  selectPCPProviderInfoLogic,
  setCheckedDependentCheckbox,
  setCheckedDependentCheckboxId,
  setCheckSubscriberExistingPatientCheckbox,
  setDependentProviderItems,
  setEnableChooseProviderButton,
  setHideSubscriberCheckboxWhenChecked,
  setOpenChoosePCPDrawer,
  setOpenSelectProviderDrawer,
  setPcpDentalData,
  setSubscriberCheckbox,
  setSubscriberProviderItems,
} from './services';
import { GoogleMap } from '../GoogleMap';
import { BenefitPlanDto, PcpMembersDto } from 'enrollment/queries/useBenefitPlansQuery/types';
import { AdditionalPlanElectionDataDto } from '../../../types';
import { PageContentQueryResponseDto } from 'enrollment/queries/usePageContentQuery/types';

interface SearchResultsSelectPCPDrawerProps {
  providerItem: any;
  index: string;
  electPlan?: () => void;
  setPcpElectionData: (data: AdditionalPlanElectionDataDto) => void;
  setPcpDrawerRequired: (value: boolean) => void;
  fromPlanDetails?: boolean;
  selectedDependentIds?: number[];
  openChooseProviderDrawer: boolean;
  setOpenChooseProviderDrawer: (value: boolean) => void;
  setShowPcpDrawer: (value: boolean) => void;
  plan?: BenefitPlanDto;
  hideDependentCheckboxWhenChecked: { [key: string]: boolean };
  setHideDependentCheckboxWhenChecked: (value: { [key: string]: boolean }) => void;
  selected: boolean;
  pcpMembers: PcpMembersDto;
  isEsignPlanRequired?: boolean;
  pageContent: PageContentQueryResponseDto | undefined;
}

export const SearchResultsSelectPCPDrawer = ({
  providerItem,
  index,
  electPlan,
  setPcpElectionData,
  setPcpDrawerRequired,
  fromPlanDetails,
  selectedDependentIds,
  openChooseProviderDrawer,
  setOpenChooseProviderDrawer,
  setShowPcpDrawer,
  plan,
  hideDependentCheckboxWhenChecked,
  setHideDependentCheckboxWhenChecked,
  selected,
  pcpMembers,
  isEsignPlanRequired,
  pageContent,
}: SearchResultsSelectPCPDrawerProps) => {

  const subscriberData = useSelector(selectSubscriberDetails);
  const dependentSelectorData = useSelector(selectDependentDetails);

  const providerSelectorData = useSelector(selectProviderApiData);

  const providerInfoLogicSelector = useSelector(selectPCPProviderInfoLogic);
  const isMobile = window.innerWidth < 744;

  const checkDependentCheckboxId =
    providerInfoLogicSelector?.dependentCheckboxCheckedId;

  const checkDependentCheckbox =
    providerInfoLogicSelector?.isDependentCheckboxChecked;

  const dependentProviderItems =
    providerInfoLogicSelector?.dependentProviderItems;

  const hideSubscriberCheckboxWhenChecked = providerInfoLogicSelector?.hideSubscriberCheckboxWhenChecked;

  const subscriberProviderDataExistingPatient =
    providerInfoLogicSelector?.isSubscriberExistingPatientCheck;

  const subscriberProviderData =
    providerInfoLogicSelector?.subscriberProviderItems;

  const dependentData = dependentSelectorData?.dependents?.filter(
    (dependent: any) => selectedDependentIds?.includes(dependent?.id)
  );

  const openSelectProviderDrawer =
    providerInfoLogicSelector?.isSelectProviderDrawerOpen;

  const dispatch = useDispatch();

  const { t } = useTranslation('enrollment.ChoosePCPDrawer');

  const handleSubscriberProvider = (e: any, providerSelectorData: any) => {
    dispatch(setSubscriberCheckbox(e?.target?.checked));
    if (e?.target?.checked) {
      dispatch(setSubscriberProviderItems(providerSelectorData));
    }

    const anyDependentChecked = Object.values({
      ...checkDependentCheckbox,
      [checkDependentCheckboxId]:
        checkDependentCheckbox[checkDependentCheckboxId],
    }).some((value) => value);

    dispatch(
      setEnableChooseProviderButton(!e?.target?.checked && !anyDependentChecked)
    );
    dispatch(setCheckSubscriberExistingPatientCheckbox(pcpMembers?.existing_patient));
  };

  const handleDependentProvider = (
    e: any,
    dependent: any,
    providerSelectorData: any
  ) => {
    if (e?.target?.tagName === 'INPUT') {
      dispatch(setCheckedDependentCheckboxId(e?.target?.id));

      dispatch(
        setCheckedDependentCheckbox({
          id: e.target.id,
          checked: e.target.checked,
        })
      );

      const newData =
        dependentProviderItems &&
        dependentProviderItems?.filter((item: any) => item.id !== dependent.id);

      const pcpMembersExistingPatient = pcpMembers?.dependents?.find((item) => { return item.id === dependent.id; });

      if (e?.target?.checked) {
        if (!selected) {
          newData.push({ ...providerSelectorData, id: dependent.id, dependent });
        }
        else {
          newData.push({ ...providerSelectorData, id: dependent.id, dependent, existingPatient: pcpMembersExistingPatient?.existing_patient });
        }
      }

      dispatch(setDependentProviderItems(newData));

      const anyDependentChecked = Object.values({
        ...checkDependentCheckbox,
        [e?.target?.id]: e?.target?.checked,
      }).some((value) => value);

      dispatch(
        setEnableChooseProviderButton(
          !anyDependentChecked &&
          !providerInfoLogicSelector?.isSubscriberCheckboxChecked
        )
      );
    }
  };

  const handleChangeSubscriberExistingPatient = (e: any) => {
    if (e?.target?.tagName === 'INPUT') {
      dispatch(setCheckSubscriberExistingPatientCheckbox(e?.target?.checked));
    }
  };

  const updateDependentData = (
    prevState: any,
    e: any,
    dependentData: any[],
    providerItem: any
  ) => {
    const newState = [...prevState]; // Clone the previous state to avoid direct mutation
    const matchingItem = pcpMembers?.dependents?.filter((dependent: any) =>
      selectedDependentIds?.includes(dependent?.id)
    );
    const dependentLoopingCondition =
      matchingItem && matchingItem?.length > 0 ? matchingItem : dependentData;

    const targetIDRegex = e?.target?.id;
    const matchTargetId = targetIDRegex?.match(/(\d+)$/)[1];

    dependentLoopingCondition?.forEach((dependent) => {
      const index = newState.findIndex(
        (item: any) => item.dependent.id === dependent.id
      );

      if (index !== -1) {
        // If the item exists, update only the existingPatient value
        const previousChecked = newState[index].existingPatient;
        newState[index] = {
          ...newState[index],
          existingPatient:
            Number(matchTargetId) === dependent.id
              ? e?.target?.checked
              : previousChecked,
        };
      } else {
        // Add as a new item if it doesn't exist
        const newItem = {
          ...providerItem,
          id: dependent?.id,
          dependent,
          existingPatient:
            Number(matchTargetId) === dependent.id
              ? e?.target?.checked
              : dependent?.existing_patient,
          providerName: dependent?.pcp_name,
          providerNetworkCode: dependent?.pcp_code,
        };
        newState.push(newItem);
      }
    });

    return newState;
  };

  const handleChangeDependentExistingPatient = (
    e: any,
    dependentData: any,
    providerItem: any
  ) => {
    if (e?.target?.tagName === 'INPUT') {
      const newState = updateDependentData(
        dependentProviderItems,
        e,
        dependentData,
        providerItem
      );
      dispatch(setDependentProviderItems(newState));
    }
  };

  const subscriberProviderDataName = subscriberProviderData?.name
    ? subscriberProviderData?.name
    : pcpMembers?.pcp_name;

  const subscriberProviderDataNetworkCode = subscriberProviderData?.network_code
    ? subscriberProviderData?.network_code
    : pcpMembers?.pcp_code;

  const dependentSelectedIdPcpData = pcpMembers?.dependents?.filter(
    (dependent: any) => selectedDependentIds?.includes(dependent?.id)
  );

  const requiredSaveCheckedDependentDataSelector =
    Array.isArray(dependentProviderItems) &&
    dependentProviderItems?.filter((dependent: any) =>
      selectedDependentIds?.includes(dependent?.id)
    );

  const pcpMembersSubmitDependentData =
    Object.keys(dependentProviderItems)?.length !== 0
      ? requiredSaveCheckedDependentDataSelector
      : dependentSelectedIdPcpData;

  const mappedPcpDependentData =
    pcpMembersSubmitDependentData && pcpMembersSubmitDependentData?.map((data: any) => ({
      id: data?.dependent?.id ? data?.dependent?.id : data?.id,
      pcpCode: data?.providerNetworkCode
        ? data?.providerNetworkCode
        : data?.network_code
          ? data?.network_code
          : data?.pcp_code,
      pcpName: data?.providerName
        ? data?.providerName
        : data?.name
          ? data?.name
          : data?.pcp_name,
      existingPatient: data?.existingPatient ? data?.existingPatient : false,
    }));

  const pcpDentalData = {
    pcpCode: subscriberProviderDataNetworkCode,
    pcpName: subscriberProviderDataName,
    existingPatient: subscriberProviderDataExistingPatient,
    dependents: mappedPcpDependentData,
  };

  const handleChooseProviderBtn = () => {
    if (!selected) {
      setOpenChooseProviderDrawer(!openChooseProviderDrawer);
      providerInfoLogicSelector?.isSubscriberCheckboxChecked && dispatch(setHideSubscriberCheckboxWhenChecked(false));

      setHideDependentCheckboxWhenChecked((prevState: { [key: string]: boolean; }) => {
        const newState = { ...prevState };
        Object.keys(checkDependentCheckbox)?.forEach(dependentId => {
          if (checkDependentCheckbox[dependentId]) {
            newState[dependentId] = true;
          }
        });
        return newState;
      });
    } else {
      dispatch(setPcpDentalData(pcpDentalData));

      dispatch(setOpenSelectProviderDrawer(false));
      dispatch(setOpenChoosePCPDrawer(false));
      const hasDrawerOpenClass = document.querySelector('.drawer-open');
      if (hasDrawerOpenClass) {
        hasDrawerOpenClass.classList.remove('drawer-open');
      }

      // handling the election and opening e-signature modal has been moved to PlanCard.tsx
      // to handle multiple popups we are just setting the additional election data here.
      setPcpElectionData({
        pcpFormData: pcpDentalData,
      });
      if (isEsignPlanRequired) {
        setPcpDrawerRequired(false);
      } else {
        // however we do need to update the provider information when we update from the plan details section
        // after initially electing.
        if (fromPlanDetails && electPlan) {
          electPlan();
        }
      }

    }
  };

  const handleSelectProviderBtn = (providerItem: any) => {
    dispatch(setProviderApi(providerItem));
    dispatch(setOpenSelectProviderDrawer(true));
  };

  const markerData = (providerData: any) => {
    return [
      {
        position: {
          lat: parseFloat(providerData?.latitude.toString()),
          lng: parseFloat(providerData?.longitude.toString()),
        },
        title: providerData?.name,
        infoPopup: `<h6>${providerData?.name}</h6>
                          <div>${providerData?.office_name}</div>
                          <div>${providerData?.street_address}</div>
                          <div>${providerData?.city_state_zip}</div>
                          <div>${providerData?.phone_number}</div>`,
      },
    ];
  };
  const isWideViewport = window.innerWidth > 1023;
  const mapStyle = isWideViewport
    ? { width: '376px', height: '330px', marginTop: '36px' }
    : { width: '239px', height: '209px', marginTop: '36px' };

  return (
    <>
      <PsButtonTuner
        data-testid="select-providers-btn"
        buttonId="select-providers-btn"
        label="Select"
        level="secondary"
        onClick={() => handleSelectProviderBtn(providerItem)}
      />
      <OrbitDrawer
        drawerId="select-providers-drawer"
        heading={t('dental.heading')}
        isOpen={openSelectProviderDrawer}
        handleClose={() => {
          dispatch(setOpenSelectProviderDrawer(false));
        }}
        closeIcon="back"
        dialogLarge
      >
        <div className="d-flex justify-content-md-between">
          <div className="left-column">
            <SearchResultsContent
              providerItem={providerSelectorData}
              index={index}
            />
            <div className="mt-5">
              <Typography variant="body1" fontWeight={600}>
                {t(
                  'dental.searchResultsSelectPCPDrawer.chooseFamilyMemberHeading'
                )}
              </Typography>
              <SearchResultFamilyProviderStyled className="d-inline-flex flex-column">
                {hideSubscriberCheckboxWhenChecked &&
                  <>
                    <ProviderFamilyBoxStyled
                      variant="outlined"
                      className="d-flex pl-2 pr-3 align-items-center mb-3"
                    >
                      <PsCheckboxTuner
                        inputId="subscriber-provider"
                        label={subscriberData?.first_name}
                        hideLabel
                        name={subscriberData?.first_name}
                        extraClass="mb-3 mr-1"
                        onClick={(e) =>
                          handleSubscriberProvider(e, providerSelectorData)
                        }
                      />
                      <div>
                        {displayDependentAvatar(
                          subscriberData?.subscriber_profile,
                          40
                        )}
                      </div>
                      <Typography variant="body1" className="ml-2">
                        {t('dental.searchResultsSelectPCPDrawer.subscriberName')}
                      </Typography>
                    </ProviderFamilyBoxStyled>
                    {selected && <PsCheckboxTuner
                      inputId="existing-patient"
                      label={t('dental.existingPatientBtnLabel')}
                      name="existing-provider"
                      checked={pcpMembers?.existing_patient}
                      onClick={handleChangeSubscriberExistingPatient}
                    />}
                  </>
                }
                {dependentData?.map((dependent: any) => {
                  const dependentId = `dependent-provider-${dependent.id}`;

                  const checkHideDependentCheckboxWhenCheckedIsFalse = Object.keys(hideDependentCheckboxWhenChecked)?.filter((key) => hideDependentCheckboxWhenChecked[key] === false);

                  const dependentPcpData =
                    Array.isArray(pcpMembers?.dependents) &&
                    pcpMembers?.dependents.find(
                      (depPcpData: any) => depPcpData.id === dependent.id
                    );

                  const checkedExistingPatient =
                    dependentProviderItems?.find(
                      (item: any) => item.id === dependent.id
                    )?.existingPatient;

                  const existingPatient =
                    checkedExistingPatient
                      ? checkedExistingPatient
                      : dependentPcpData && dependentPcpData?.existing_patient;

                  return (
                    <>
                      {((hideDependentCheckboxWhenChecked && (Object.keys(hideDependentCheckboxWhenChecked)?.length === 0)) || checkHideDependentCheckboxWhenCheckedIsFalse?.includes(dependentId)) &&
                        <>
                          <ProviderFamilyBoxStyled
                            variant="outlined"
                            key={`dependent-drawer-data-${dependent.id}`}
                            className="d-flex pl-2 pr-3 align-items-center mb-3"
                          >
                            <PsCheckboxTuner
                              inputId={dependentId}
                              label={dependent.first_name}
                              hideLabel
                              name={dependent.first_name}
                              extraClass="mb-3 mr-1"
                              value={checkDependentCheckbox[dependentId]}
                              onClick={(e) =>
                                handleDependentProvider(
                                  e,
                                  dependent,
                                  providerSelectorData
                                )
                              }
                            />
                            <div>
                              {displayDependentAvatar(
                                dependent?.dependent_profile,
                                40
                              )}
                            </div>
                            <Typography
                              variant="body1"
                              className="ml-2"
                            >{`${dependent.first_name} ${getDependentAge(dependent.birthdate) !== 0 ? `(${getDependentAge(dependent.birthdate)})` : ''}`}</Typography>
                          </ProviderFamilyBoxStyled>
                          {selected && <PsCheckboxTuner
                            inputId={`existing-patient-btn-${dependentId}`}
                            label={t('dental.existingPatientBtnLabel')}
                            name="existing-provider"
                            checked={Boolean(existingPatient)}
                            onClick={(e) =>
                              handleChangeDependentExistingPatient(
                                e,
                                dependentData,
                                providerItem
                              )
                            } />
                          }
                        </>
                      }
                    </>
                  );
                })}
              </SearchResultFamilyProviderStyled>
            </div>
          </div>
          {!isMobile && (
            <div className="right-column">
              {providerSelectorData &&
                Object.keys(providerSelectorData).length > 0 && (
                  <GoogleMap
                    style={mapStyle}
                    maxInitialZoom={15}
                    id="singleProviderGoogleMap"
                    markers={markerData(providerSelectorData)}
                  />
                )}
            </div>
          )}
        </div>
        <hr className="mr-1 ml-1" />
        <div className="text-right">
          <PsButtonTuner
            buttonId="providers-info-drawer-btn"
            label="Choose Provider"
            level="primary"
            onClick={handleChooseProviderBtn}
            data-testid="providers-info-drawer-btn"
            disabled={providerInfoLogicSelector?.isEnableChooseProviderBtn}
          />
          <ProvideInfoDrawer
            setPcpElectionData={setPcpElectionData}
            setPcpDrawerRequired={setPcpDrawerRequired}
            pcpMembers={{
              pcp_code: '',
              pcp_name: '',
              existing_patient: undefined,
              dependents: [],
            }}
            selectedDependentIds={selectedDependentIds}
            openChooseProviderDrawer={openChooseProviderDrawer}
            setOpenChooseProviderDrawer={setOpenChooseProviderDrawer}
            setShowPcpDrawer={setShowPcpDrawer}
            plan={plan}
            setHideDependentCheckboxWhenChecked={setHideDependentCheckboxWhenChecked}
            pageContent={pageContent}
          />
        </div>
      </OrbitDrawer>
    </>
  );
};
